<div class="telemetry-diagnostic-panel">
  <h4>Telemetry diagnostic</h4>

  <div class="diagnostic-banner">
    Diagnostic — test data, not used for calculations.
  </div>

  <p class="muted small">
    Confirm {{ providerLabel }} telemetry is reaching Harvest Mate: test the
    connection, list equipment, run a connectivity check, and view the
    persisted canonical rows that the DSS adapter has stored.
  </p>

  <div class="actions">
    <button
      class="btn-small btn-primary"
      type="button"
      (click)="testConnection()"
      [disabled]="isTesting"
    >
      {{ isTesting ? 'Testing…' : 'Test connection' }}
    </button>
    <button
      class="btn-small btn-left-space"
      type="button"
      (click)="loadEquipment()"
      [disabled]="isLoadingEquipment"
    >
      {{ isLoadingEquipment ? 'Loading…' : 'Equipment' }}
    </button>
    <button
      class="btn-small btn-left-space"
      type="button"
      (click)="pullTestData()"
      [disabled]="isPulling"
    >
      {{ isPulling ? 'Pulling…' : 'Pull test data' }}
    </button>
    <button
      class="btn-small btn-left-space"
      type="button"
      (click)="loadPersistedTelemetry()"
      [disabled]="isLoadingPersisted"
    >
      {{ isLoadingPersisted ? 'Loading…' : 'View persisted telemetry' }}
    </button>
  </div>

  <!-- Action 1 — Test connection result -->
  @if (testResult) {
  <div
    class="diagnostic-result margin-top-small"
    [class.result-ok]="testResult.ok"
    [class.result-error]="!testResult.ok"
  >
    @if (testResult.ok) {
    <span class="status-chip status-connected">OK &mdash; {{ providerLabel }}</span>
    } @else {
    <span class="status-chip status-verification-needed">
      Failed{{ testResult.detail ? ' — ' + testResult.detail : '' }}
    </span>
    }
    <div class="muted small">Last checked: {{ testResult.lastChecked }}</div>
  </div>
  }

  <!-- Action 2 — Equipment -->
  @if (equipmentLoaded) {
  <div class="diagnostic-section margin-top-small">
    <h5>Equipment</h5>
    @if (equipmentError) {
    <div class="muted small">{{ equipmentError }}</div>
    } @else if (machines.length === 0) {
    <div class="muted small">No equipment reported.</div>
    } @else {
    <ul class="equipment-list">
      @for (m of machines; track m.oem_machine_id) {
      <li>
        <span class="machine-name">{{ m.oem_machine_name }}</span>
        <span class="muted small"> ({{ m.oem_machine_id }})</span>
        @if (m.linked_harvester_id != null) {
        <span class="status-chip status-connected"
          >linked &rarr; harvester #{{ m.linked_harvester_id }}</span
        >
        } @else {
        <span class="status-chip status-verification-needed">unlinked</span>
        }
      </li>
      }
    </ul>
    }
  </div>
  }

  <!-- Action 3 — Pull test data → canonical summary returned inline -->
  @if (operationsLoaded) {
  <div class="diagnostic-section margin-top-small">
    <h5>Connectivity check — latest operation</h5>
    <p class="muted small">
      Confirms we can reach the provider and shows the latest field operation
      with its harvest metrics (SRA003-447). Values render '—' until the
      provider populates them.
    </p>
    @if (operationsError) {
    <div class="muted small">{{ operationsError }}</div>
    } @else if (operations.length === 0) {
    <div class="muted small">No field operations returned yet.</div>
    } @else {
    <div class="table-scroll">
      <table class="diagnostic-table">
        <thead>
          <tr>
            <th>Started</th>
            <th>Ended</th>
            <th>Harvester</th>
            <th>Block</th>
            <th>Cane yield (t/ha)</th>
            <th>Ground speed (km/h)</th>
            <th>Trash %</th>
            <th>Moisture %</th>
            <th>Fan rpm</th>
            <th>Provider</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          @for (op of operations; track op.id) {
          <tr>
            <td>{{ op.operation_started_at ?? '—' }}</td>
            <td>{{ op.operation_ended_at ?? '—' }}</td>
            <td>{{ op.harvester_id != null ? op.harvester_id : '—' }}</td>
            <td>{{ op.block_id != null ? op.block_id : '—' }}</td>
            <td>
              {{
                op.cane_yield_tha != null
                  ? (op.cane_yield_tha | number : '1.2-2')
                  : '—'
              }}
            </td>
            <td>
              {{
                op.ground_speed_kmh != null
                  ? (op.ground_speed_kmh | number : '1.1-1')
                  : '—'
              }}
            </td>
            <td>
              {{ op.trash_pct != null ? (op.trash_pct | number : '1.1-1') : '—' }}
            </td>
            <td>
              {{
                op.moisture_pct != null
                  ? (op.moisture_pct | number : '1.1-1')
                  : '—'
              }}
            </td>
            <td>
              {{
                op.fan_speed_rpm != null
                  ? (op.fan_speed_rpm | number : '1.0-0')
                  : '—'
              }}
            </td>
            <td>{{ op.provider }}</td>
            <!-- SRA003-519: per-machine freshness chip (vendor-agnostic). -->
            <td>
              <span
                class="freshness-chip freshness-{{ freshness(op) }}"
                [title]="op.last_seen || 'No telemetry yet'"
              >
                {{ freshnessLabel(op) }}
              </span>
            </td>
          </tr>
          }
        </tbody>
      </table>
    </div>
    }
  </div>
  }

  <!-- Action 4 — Persisted telemetry (FE-190 Stage 2 / real DSS events) -->
  @if (persistedLoaded) {
  <div class="diagnostic-section margin-top-small">
    <h5>Persisted telemetry (real DSS events)</h5>
    @if (persistedError) {
    <div class="muted small">{{ persistedError }}</div>
    } @else if (persistedItems.length === 0) {
    <div class="muted small">
      No persisted telemetry in the last 30 days. Live data arrives after
      enabling the DSS subscription and the next Deere harvest activity.
    </div>
    } @else {
    <div class="table-scroll">
      <table class="diagnostic-table">
        <thead>
          <tr>
            <th>Started</th>
            <th>Cane yield (t/ha)</th>
            <th>Ground speed (km/h)</th>
            <th>Trash %</th>
            <th>Moisture %</th>
            <th>Fan RPM</th>
            <th>Samples</th>
            <th>Provider</th>
          </tr>
        </thead>
        <tbody>
          @for (op of persistedItems; track op.id) {
          <tr>
            <td>{{ op.operation_started_at ?? '—' }}</td>
            <td>
              {{
                op.cane_yield_tha != null
                  ? (op.cane_yield_tha | number : '1.2-2')
                  : '—'
              }}
            </td>
            <td>
              {{
                op.ground_speed_kmh != null
                  ? (op.ground_speed_kmh | number : '1.1-1')
                  : '—'
              }}
            </td>
            <td>
              {{
                op.trash_pct != null
                  ? (op.trash_pct | number : '1.1-1')
                  : '—'
              }}
            </td>
            <td>
              {{
                op.moisture_pct != null
                  ? (op.moisture_pct | number : '1.1-1')
                  : '—'
              }}
            </td>
            <td>
              {{
                op.fan_speed_rpm != null
                  ? (op.fan_speed_rpm | number : '1.0-0')
                  : '—'
              }}
            </td>
            <td>{{ op.sample_count != null ? op.sample_count : '—' }}</td>
            <td>{{ op.provider }}</td>
          </tr>
          }
        </tbody>
      </table>
    </div>
    }
  </div>
  }
</div>
